import React, { useState, useEffect } from "react";
import arrowIcon from "../../assets/icons/arrow.png";
import { articleHomeData } from "../../data/articleHome.data";
import { Link, useNavigate } from "react-router-dom";
import routes from "../../routes/routes";
import CalendarIcone from '../../assets/icons/calendar.png';
import { getFourNewArticle } from "../../services/article.service";
import {useDateFormatter} from "../../hooks";

const ArticleSection = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const formatDate = useDateFormatter();

    const { featuredArticle: staticFeaturedArticle, secondaryArticles: staticSecondaryArticles, buttonText } = articleHomeData;

    const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                setLoading(true);
                const response = await getFourNewArticle();

                if (response && response.data && Array.isArray(response.data)) {
                    setArticles(response.data);
                } else if (response && Array.isArray(response)) {
                    setArticles(response);
                } else {
                    console.warn("Format de réponse API inattendu:", response);
                    setArticles([]);
                }
                setError(false);
            } catch (err) {
                console.error("Erreur lors de la récupération des articles:", err);
                setError(true);
                setArticles([]);
            } finally {
                setLoading(false);
            }
        };

        fetchArticles();
    }, []);

    const formatRubriques = (rubriques) => {
        if (!rubriques || !Array.isArray(rubriques) || rubriques.length === 0) {
            return '';
        }

        return rubriques.map(rubrique => rubrique.name_rub).join(', ');
    };

    const formatAuthorName = (author, isMobile = false) => {
        if (!author) return "";
        if (typeof author === "string") return author;

        const firstName = author.first_name || "";
        const lastName = author.last_name || "";

        if (isMobile || (firstName.length + lastName.length > 20)) {
            const initials = firstName
                .split(' ')
                .map(part => part.charAt(0) + '.')
                .join(' ');

            const truncatedLastName = lastName.length > 12
                ? `${lastName.substring(0, 10)}...`
                : lastName;

            return `${initials} ${truncatedLastName}`;
        }

        return `${firstName} ${lastName}`;
    };

    const getImagePath = (photoPath) => {
        if (!photoPath) return '';
        return photoPath.startsWith('http') ? photoPath : `${REACT_APP_BASE_URL}/${photoPath}`;
    };

    const useStaticData = articles.length < 3 || error;

    const displayArticles = !useStaticData ? articles : [
        staticFeaturedArticle,
        ...staticSecondaryArticles
    ];

    const featuredArticle = displayArticles[0];
    const secondaryArticles = displayArticles.slice(1, 3);
    const fourthArticle = displayArticles[3] || featuredArticle;

    return (
        <div className="w-full px-4 py-6 sm:px-6 max-w-7xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-greenprimary flex items-center mb-4">
                <span className="w-10 sm:w-16 h-1.5 sm:h-2.5 mt-2 sm:mt-3 bg-greenprimary mr-2"></span>
                Article à la une
            </h2>

            {loading ? (
                <div className="flex justify-center items-center h-[400px]">
                    <div className="w-12 h-12 border-4 border-greenlogo border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
                    {featuredArticle && (
                        <div
                            className="col-span-4 sm:col-span-8 lg:col-span-6 lg:row-span-6 aspect-square cursor-pointer"
                            onClick={() => navigate(`/article/${!useStaticData ? featuredArticle.slug : featuredArticle.id}`)}
                        >
                            <div
                                className="w-full h-full bg-inputbackground flex items-center justify-center relative overflow-hidden"
                                style={!useStaticData && featuredArticle.photo_art ? {
                                    backgroundImage: `url(${getImagePath(featuredArticle.photo_art)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                } : {}}
                            >
                                <div className="absolute inset-0 bg-black bg-opacity-65"></div>
                                <div className="absolute top-3 left-3">
                                    <span className="text-xs bg-danger text-white px-2 py-1 w-fit">
                                        {!useStaticData
                                            ? formatRubriques(featuredArticle.rubriques)
                                            : featuredArticle.rubrique}
                                    </span>
                                </div>
                                <div className="absolute inset-0 p-3 sm:p-4 flex flex-col justify-end">
                                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-white mt-1">
                                        <span className="truncate max-w-[120px] sm:max-w-[180px] lg:max-w-[250px]" title={!useStaticData ? `${featuredArticle.author?.first_name || ''} ${featuredArticle.author?.last_name || ''}` : featuredArticle.auteur}>
                                            {!useStaticData
                                                ? formatAuthorName(featuredArticle.author, window.innerWidth < 640)
                                                : featuredArticle.auteur}
                                        </span>
                                        <span>-</span>
                                        <img src={CalendarIcone} alt="calendar icone" className="w-4 h-4" />
                                        <span className="whitespace-nowrap">
                                            {!useStaticData
                                                ? formatDate(featuredArticle.date_pub_art)
                                                : featuredArticle.date}
                                        </span>
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-semibold text-white">
                                        {!useStaticData
                                            ? (featuredArticle.title_art?.fr || '')
                                            : featuredArticle.title}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    )}

                    {secondaryArticles.map((article, index) => (
                        <div
                            key={!useStaticData ? article.id_art : article.id}
                            className={`col-span-4 sm:col-span-4 lg:col-span-3 ${
                                index === 0 ? 'lg:col-start-7' : 'lg:col-start-10'
                            } cursor-pointer`}
                            onClick={() => navigate(`/article/${!useStaticData ? article.slug : article.id}`)}
                        >
                            <div
                                className="w-full h-full aspect-square bg-inputbackground flex items-center justify-center relative overflow-hidden"
                                style={!useStaticData && article.photo_art ? {
                                    backgroundImage: `url(${getImagePath(article.photo_art)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                } : {}}
                            >
                                <div className="absolute inset-0 bg-black bg-opacity-65"></div>
                                <div className="absolute top-3 left-3">
                                    <span className="text-xs bg-danger text-white px-2 py-1 w-fit">
                                        {!useStaticData
                                            ? formatRubriques(article.rubriques)
                                            : article.rubrique}
                                    </span>
                                </div>
                                <div className="absolute inset-0 p-3 flex flex-col justify-end">
                                    <div className="flex items-center gap-1.5 flex-wrap text-xs sm:text-sm text-white mt-1">
                                        <span className="truncate max-w-[120px]" title={!useStaticData ? `${article.author?.first_name || ''} ${article.author?.last_name || ''}` : article.auteur}>
                                            {!useStaticData
                                                ? formatAuthorName(article.author, true)
                                                : article.auteur}
                                        </span>
                                        <span>-</span>
                                        <img src={CalendarIcone} alt="calendar icone" className="w-4 h-4" />
                                        <span className="whitespace-nowrap">
                                            {!useStaticData
                                                ? formatDate(article.date_pub_art)
                                                : article.date}
                                        </span>
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-white">
                                        {!useStaticData
                                            ? (article.title_art?.fr || '')
                                            : article.title}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    ))}

                    {fourthArticle && (
                        <div
                            className="col-span-4 sm:col-span-8 lg:col-span-6 lg:col-start-7 lg:row-start-2 lg:row-span-5 h-auto cursor-pointer"
                            onClick={() => navigate(`/article/${!useStaticData ? fourthArticle.slug : fourthArticle.id}`)}
                        >
                            <div
                                className="w-full h-full min-h-[12rem] sm:min-h-[16rem] lg:min-h-0 bg-inputbackground p-3 sm:p-4 flex flex-col justify-between  relative"
                                style={!useStaticData && fourthArticle.photo_art ? {
                                    backgroundImage: `url(${getImagePath(fourthArticle.photo_art)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                } : {}}
                            >
                                <div className="absolute inset-0 bg-black bg-opacity-65"></div>
                                <div className="relative z-10">
                                    <span className="text-xs bg-danger text-white px-2 py-1 w-fit">
                                        {!useStaticData
                                            ? formatRubriques(fourthArticle.rubriques)
                                            : fourthArticle.rubrique}
                                    </span>
                                </div>
                                <div className="relative z-10">
                                    <div className="flex items-center gap-1.5 flex-wrap text-xs sm:text-sm text-white mt-1">
                                        <span className="truncate max-w-[120px] sm:max-w-[200px]" title={!useStaticData ? `${fourthArticle.author?.first_name || ''} ${fourthArticle.author?.last_name || ''}` : fourthArticle.auteur}>
                                            {!useStaticData
                                                ? formatAuthorName(fourthArticle.author, window.innerWidth < 640)
                                                : fourthArticle.auteur}
                                        </span>
                                        <span>-</span>
                                        <img src={CalendarIcone} alt="calendar icone" className="w-4 h-4" />
                                        <span className="whitespace-nowrap">
                                            {!useStaticData
                                                ? formatDate(fourthArticle.date_pub_art)
                                                : fourthArticle.date}
                                        </span>
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-semibold text-white">
                                        {!useStaticData
                                            ? (fourthArticle.title_art?.fr || '')
                                            : fourthArticle.title}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="col-span-4 sm:col-span-8 lg:col-span-3 lg:col-start-10 lg:row-start-7 mt-2 sm:mt-3 lg:mt-2 flex justify-center lg:justify-end">
                        <Link to={routes.search} className="bg-greenlogo font-bold w-full sm:w-auto text-white px-3 sm:px-5 py-4 sm:py-4.5 flex items-center justify-center sm:justify-start text-xs sm:text-sm transition-all hover:bg-opacity-90">
                            {buttonText}
                            <img src={arrowIcon} className="ml-2 sm:ml-3 h-3 w-auto sm:h-4" alt="Voir plus" />
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleSection;